import { Annotation } from "../../tools/index"
import { Point, Rectangle } from "../primitives"
import { Format } from "./format"
import { Stroke } from "./stroke"

/**
 * @returns {Number} Returns the integer part of value, without decimals, rounded to lower.
 */
function trunc(value) {
	return value | 0
}

export class LegendSymbol {
	constructor(chart) {
		this.chart = chart

		this.format = new Format(chart)
		this.format.shadow.visible = true
		this.format.shadow.color = "silver"
		this.format.shadow.width = 2
		this.format.shadow.height = 2
		this.format.depth = 0.01

		this.width = 8
		this.height = 8
		this.padding = 8
		this.style = "rectangle"
		this.visible = true
	}

	tryHover(series, index) {
		if (series.hover.enabled) {
			const showValues = this.chart.legend.showValues()

			if ((showValues && series.over === index) || (!showValues && series.over >= 0)) {
				return series.hover
			}
		}

		return null
	}

	draw(series, index, x, y) {
		const c = this.chart.ctx
		const f = this.format
		const fhover = this.tryHover(series, index)
		const oldFill = f.fill
		const oldStroke = f.stroke

		f.fill = series.legendColor(index)
		c.z = -0.01

		switch (this.style) {
			case "rectangle":
				if (fhover) {
					f.stroke = fhover.stroke
				}

				if (this.chart.aspect.view3d) {
					const r = {
						x,
						y: y - this.height * 0.5 - 1,
						width: this.width,
						height: this.height
					}

					const oldZ = f.z
					const oldCtxZ = c.z

					f.z = c.z - f.depth
					f.cube(r)
					f.draw(c, null, r)

					f.z = oldZ
					c.z = oldCtxZ
				} else {
					f.rectangle(x, y - this.height * 0.5 - 1, this.width, this.height)
				}
				break

			case "triangle":
				if (fhover) {
					f.stroke = fhover.stroke
				}

				f.polygon([
					new Point(x + this.width * 0.5, y - this.height * 0.5),
					new Point(x + this.width, y + this.height * 0.5),
					new Point(x, y + this.height * 0.5)
				])
				break

			case "ellipse":
				if (fhover) {
					f.stroke = fhover.stroke
				}

				f.ellipse(x + this.width * 0.5, y, this.width, this.height)
				break

			default:
				c.beginPath()
				c.moveTo(x, y)
				c.lineTo(x + this.width, y)

				if (fhover) {
					fhover.stroke.prepare()
				} else {
					f.stroke.prepare(f.fill)
				}

				c.stroke()
				break
		}

		f.fill = oldFill
		f.stroke = oldStroke
	}
}


/**
 * @memberOf Tee.Chart
 * @constructor
 * @class Displays a list of chart series data
 * @param {Tee.Chart} chart The parent chart this legend object belongs to.
 * @property {Number} align Legend position as offset % of chart size. Default 0.
 * @property {Number} padding Percent of chart size pixels to leave as margin from legend.
 * @property {Boolean} transparent Determines to draw or not the legend background.
 * @property {Format} format Formatting properties to draw legend background and items.
 * @property {Format} hover Limited Formatting properties for mouseover hover of text. Offers color and enabled.
 * @property {Annotation} title Draws a title on top of legend.
 * @property {Rectangle} bounds Defines the legend position in pixels.
 * @property {String} position Automatic position legend ("left", "top", "right" or "bottom").
 * @property {Boolean} visible Defines to draw or not the legend.
 * @property {Boolean} inverted When true, legend items are displayed in inverted order.
 * @property {Boolean} fontColor Determines to fill each legend item text using series or point colors.
 * @property {Stroke} dividing Draws a line between legend items.
 * @property {LegendSymbol} symbol Properties to draw a small indicator next to each legend item.
 * @property {String} legendStyle Determines to draw all visible series names or first visible series points. ("auto", "series", "values").
 * @property {String} activeStyle Determines the style to draw legend items when series are clicked. ("none", "checkbox", "lineThorugh", "opacity").
 * @property {String} textStyle What to draw at each legend item for series values: "auto", "valuelabel", "label", "value", "percent", "index", "labelvalue", "percentlabel", "valuepercent"
 */
export function Legend(chart) {
	this.chart = chart

	this.transparent = false

	this.useStrokeColor = false

	this.activeStyle = "none"

	var f = (this.format = new Format(chart))
	f.fill = "white"
	f.round.x = 8
	f.round.y = 8
	f.font.baseLine = "top"
	f.shadow.visible = true

	f.z = 0
	f.depth = 0.05

	this.title = new Annotation(chart)
	this.title.transparent = true

	this.bounds = new Rectangle()
	this.position = "right" // left, top, right, bottom, custom
	this.visible = true
	this.inverted = false

	this.padding = 5 // margin from legend to axes edge, percent of legend size

	this.margin = 5 // margin from legend to chart edge, percent of legend size

	this.align = 0 // % default = 0

	this.fontColor = false

	var d = (this.dividing = new Stroke(chart))
	d.fill = "" //"rgb(220,220,220)";
	d.cap = "butt"

	this.over = -1

	/*
		* Contains properties to paint a visual representation near each legend item.
		*/
	this.symbol = new LegendSymbol(chart)

	this.itemHeight = 10
	this.innerOff = 0

	this.legendStyle = "auto" // auto, series, values, ...
	this.textStyle = "auto" // valuelabel, label, value, percent, index, labelvalue, percentlabel

	this.hover = new Format(chart)
	this.hover.enabled = true
	this.hover.font.fill = "red"

	/**
	 * @returns {Number} Returns the width in pixels of legend items, including text and symbols if visible.
	 */
	this.totalWidth = function () {
		var w = itemWidth + 8,
			s = this.symbol
		if (s.visible) w += s.width + s.padding
		return w
	}

	var titleHeight = 0

	this._space = function () {
		return this.bounds.y + titleHeight + this.margin * chart.bounds.height * 0.01
	}

	/**
	 * @returns {Number} Returns the maximum number of vertical rows using the available height.
	 */
	this.availRows = function () {
		var h = this._space() + this.itemHeight * 0.5
		if (!h) h = 0
		return trunc((chart.bounds.getBottom() - h) / this.itemHeight)
	}

	function seriesCount(legend) {
		return legend.showHidden ? chart.series.items.length : chart.series.visibleCount()
	}

	/**
	 * @returns {Number} Returns the number of legend items that should be displayed.
	 */
	this.itemsCount = function () {
		var ss = chart.series,
			result = seriesCount(this),
			rr = chart.bounds

		if (result === 0) return 0

		var st = this.legendStyle

		if (st === "values" && result > 0) result = ss.firstVisible().legendCount()
		else {
			if ((st === "auto" && result > 1) || st === "series") {
				var t
				for (t = 0; t < ss.items.length; t++) if (!ss.items[t].legend.visible) result--
			} else if (result == 1) result = ss.firstVisible().legendCount()
		}

		if (this.isVertical()) {
			if ((0.5 + result) * this.itemHeight > rr.height - this._space()) result = this.availRows()
		} else {
			this.rows = 1

			var total = this.totalWidth(),
				pad = this.calcPadding(rr)

			var chartW = rr.width - 2 * pad

			if (result * total > chartW) {
				var w = rr.x + pad
				if (!w) w = 0

				this.perRow = trunc(chartW / total)

				if (result > this.perRow) {
					this.rows = 1 + trunc(result / this.perRow)

					if (this.rows * this.itemHeight > rr.height - this.bounds.y) {
						this.rows = this.availRows()
						result = this.rows * this.perRow
					}
				}
			} else this.perRow = result
		}

		return result
	}

	/**
	 * @returns {Boolean} Returns if legend shows series titles or a series values.
	 */
	this.showValues = function () {
		return this.legendStyle === "values" || (this.legendStyle === "auto" && seriesCount(this) == 1)
	}

	/**
	 * @returns {String} Returns the index'th legend text string.
	 */
	this.itemText = function (series, index) {
		var res = series.legendText(index, this.textStyle, false, true)
		return this.ongettext ? this.ongettext(this, series, index, res) : res
	}

	this.calcItemPos = function (index, pos) {
		var i = this.itemHeight,
			b = this.bounds
		pos.x = b.x
		pos.y = b.y + this.innerOff

		if (this.isVertical()) {
			pos.x += b.width - 6 - this.innerOff
			pos.y += i * 0.4 + index * i + titleHeight
		} else {
			pos.x += this.innerOff + (1 + (index % this.perRow)) * this.totalWidth()
			pos.y += i * (trunc(index / this.perRow) + 0.25)
		}
	}

	this.calcItemRect = function (index, r) {
		var i = this.itemHeight,
			b = this.bounds
		r.height = i
		r.x = b.x
		r.y = b.y

		if (this.isVertical()) {
			r.width = b.width
			r.y += i * 0.4 + index * i + titleHeight
		} else {
			r.width = this.totalWidth()
			r.x += this.innerOff + (index % this.perRow) * r.width
			r.y += i * (trunc(index / this.perRow) + 0.25)
		}
	}

	var rMouse = new Rectangle()

	this.mousedown = function (p) {
		if (this.over !== -1 && this.activeStyle !== "none") {
			for (let t = 0; t < chart.series.items.length; t++) {
				const currentSeries = chart.series.items[t]
				if (t === this.over) {
					currentSeries.visible = !currentSeries.visible
					chart.draw()
					break
				}
			}
		}

		if (this.onclick && this.over !== -1) {
			if (this.showValues()) {
				this.onclick(chart.series.firstVisible(), this.over)
			} else {
				var ss = chart.series.items,
					l = ss.length,
					c = 0
				for (var t = 0; t < l; t++)
					if (this.showHidden || ss[t].visible) {
						if (c == this.over) {
							this.onclick(ss[t], -1)
							break
						} else c++
					}
			}

			return true
		}

		return false
	}

	this.mousemove = function (p) {
		var n = this.over

		if (this.bounds.contains(p)) {
			var c = this.itemsCount()
			for (var t = 0; t < c; t++) {
				this.calcItemRect(t, rMouse)
				if (rMouse.contains(p)) {
					n = t
					break
				}
			}
		} else n = -1

		if (n != this.over) {
			if (this.onhover) this.onhover(this.over, n)

			this.over = n

			var o = this.chart
			requestAnimationFrame(() => {
				o.draw()
			})
		}

		if (this.onclick) this.chart.newCursor = n === -1 ? "default" : "pointer"
	}

	this.drawSymbol = function (series, index, itemPos) {
		this.symbol.draw(
			series,
			index,
			itemPos.x - itemWidth - this.symbol.width - this.symbol.padding,
			itemPos.y + this.itemHeight * 0.4
		)
	}

	var itemPos = { x: 0, y: 0 },
		r = new Rectangle(),
		aligns

	this.drawItem = function (text, series, index, isSeries) {
		var stopLegend = false

		var vertical = this.isVertical()

		// Add active style to legend item
		this.showHidden = this.activeStyle !== "none"

		this.calcItemPos(index, itemPos)

		r.x = itemPos.x
		r.y = itemPos.y

		if (vertical) r.y -= this.chart.isMozilla ? 0 : 2
		else if (this.chart.isMozilla) r.y++
		else r.y--

		var old = f.font.fill

		if (!series.visible && this.activeStyle === "opacity") {
			f.font.fill = "silver"
		} else {
			if (this.over == index) f.font.fill = this.hover.enabled ? this.hover.font.fill : old
			else if (this.fontColor) f.font.fill = this.showValues() ? series.legendColor(index) : series.format.fill
		}

		if (this.activeStyle === "linethrough" && !series.visible) f.font.lineThrough = true
		else f.font.lineThrough = false

		var c = this.chart.ctx

		if (isSeries) {
			f.font.textAlign = "start"
			r.x -= itemWidths[0]
			c.textAlign = f.font.textAlign

			var symbolWidth = this.symbol.width

			var getLegendArgs = { text, index, r, symbolWidth, stopLegend: false }

			if (this.onbeforedrawLegendItem) this.onbeforedrawLegendItem(getLegendArgs)

			if (getLegendArgs.stopLegend) stopLegend = true

			text = getLegendArgs.text

			const prevStrokStyle = f.stroke.fill
			if (!this.showValues()) {
				if (this.activeStyle === "checkbox") {
					const x = r.x - 2,
						y = r.y + 4

					c.beginPath()
					c.moveTo(x, y)
					c.lineTo(x + 8.5, y)
					c.lineTo(x + 8.5, y + 8.5)
					c.lineTo(x, y + 8.5)
					c.lineTo(x, y)
					// set stroke color to series color
					c.strokeStyle = "black"
					c.stroke()
					c.closePath()

					// add 10 to x to avoid overlapping with the text
					r.x += 12

					// draw a checkmark if the series is visible
					if (series.visible) {
						c.beginPath()
						c.moveTo(x + 2, y + 4)
						c.lineTo(x + 4, y + 6)
						c.lineTo(x + 7, y + 3)
						c.stroke()
						c.closePath()
					}
				}
			}

			c.strokeStyle = prevStrokStyle

			if (!stopLegend) f.drawText(r, text)
		} else {
			if (!(text instanceof Array)) text = [text]

			if (vertical) {
				var l = text.length,
					oldW = r.width,
					sep = f.textWidth(" ")
				while (l--) {
					f.font.textAlign = aligns[l] ? "start" : "end"
					r.x -= itemWidths[l]

					c.textAlign = f.font.textAlign

					var symbolWidth = this.symbol.width

					var getLegendArgs = {
						text,
						index,
						r,
						symbolWidth,
						stopLegend: false
					}

					if (this.onbeforedrawLegendItem) this.onbeforedrawLegendItem(getLegendArgs)

					if (getLegendArgs.stopLegend) stopLegend = true

					text = getLegendArgs.text

					if (!stopLegend) f.drawText(r, text[l])

					r.width -= itemWidths[l] + sep
				}
				r.width = oldW
			} else {
				f.font.textAlign = "start"
				r.x -= itemWidth
				c.textAlign = f.font.textAlign

				var symbolWidth = this.symbol.width

				var getLegendArgs = {
					text,
					index,
					r,
					symbolWidth,
					stopLegend: false
				}

				if (this.onbeforedrawLegendItem) this.onbeforedrawLegendItem(getLegendArgs)

				if (getLegendArgs.stopLegend) stopLegend = true

				text = getLegendArgs.text

				if (!stopLegend) f.drawText(r, text.join(" "))
			}
		}

		if (!stopLegend) {
			f.font.fill = old

			var hassymbol = !series.isColorEach || this.showValues()

			if (this.symbol.visible && hassymbol) this.drawSymbol(series, index, itemPos)

			if (index > 0 && this.dividing.fill !== "") {
				var b = this.bounds
				c.beginPath()

				if (this.isVertical()) {
					c.moveTo(b.x, r.y - 2)
					c.lineTo(b.getRight(), r.y - 2)
				} else {
					var xx = r.x - itemWidth - 4,
						sy = this.symbol
					if (sy.visible) xx -= sy.width + sy.padding
					c.moveTo(xx, b.y)
					c.lineTo(xx, b.getBottom())
				}

				this.dividing.prepare()
				c.stroke()
			}
		}
	}

	/**
	 * @returns {Boolean} Returns if index'th series is visible and has been displayed at legend.
	 */
	this.drawSeries = function (index, order) {
		const currentSeries = chart.series.items[index]
		if ((this.showHidden || currentSeries.visible) && currentSeries.legend.visible) {
			this.drawItem(currentSeries.titleText(index), currentSeries, order, true)
			return true
		} else return false
	}

	this.draw = function () {
		var c = this.itemsCount(),
			len,
			t,
			ti = this.title,
			ctx = chart.ctx,
			old,
			ft = this.format.transparency,
			vertical = this.isVertical()

		if (c > 0) {
			var groups = ctx.beginParent
			this.visual = groups ? ctx.beginParent() : null

			f.cube(this.bounds) //avoids first Legend item being dropped from format in WebGL transparent Legends

			if (!this.transparent) {
				f.draw(ctx, null, this.bounds)
			}

			if (ft > 0) {
				old = ctx.globalAlpha
				ctx.globalAlpha = (1 - ft) * old
			}

			if (vertical && titleHeight > 0) {
				ti.bounds.x = this.bounds.x - 4
				ti.bounds.y = this.bounds.y
				ti.doDraw()
			}

			f.font.prepare()

			r.width = itemWidth
			r.height = this.itemHeight

			if (this.showValues()) {
				var s = chart.series.firstVisible(),
					order = 0
				len = c

				switch (this.textStyle) {
					case "auto":
					case "percentlabel":
					case "valuelabel":
						aligns = [false, true]
						break
					case "labelpercent":
					case "labelvalue":
						aligns = [true, false]
						break
					case "label":
						aligns = [true]
						break
					case "valuepercent":
						aligns = [true, false]
						break
					default:
						aligns = [false]
						break
				}

				if (this.inverted) while (len--) this.drawItem(this.itemText(s, len), s, order++)
				else for (t = 0; t < len; t++) this.drawItem(this.itemText(s, t), s, t)
			} else {
				len = chart.series.count()
				if (vertical) len = Math.min(len, this.availRows())
				c = 0

				aligns = [true]

				if (this.inverted)
					while (len--) {
						if (this.drawSeries(len, c)) c++
					}
				else for (t = 0; t < len; t++) if (this.drawSeries(t, c)) c++
			}

			if (ft > 0) ctx.globalAlpha = old

			if (groups) ctx.endParent()
		}
	}

	var itemWidth, itemWidths

	/**
	 * @returns {Number} Returns the maximum width in pixels of all legend items text.
	 */
	this.calcWidths = function () {
		var s,
			t,
			d,
			c,
			l = chart.series,
			text

		itemWidth = 0
		itemWidths = [0, 0]

		if (this.showValues()) {
			s = l.firstVisible()
			c = this.itemsCount()

			for (t = 0; t < c; t++) {
				text = this.itemText(s, t)
				if (!(text instanceof Array)) text = [text]

				if (text.length > 0) {
					d = f.textWidth(text[0])
					if (d > itemWidths[0]) itemWidths[0] = d

					if (text.length > 1) {
						d = f.textWidth(text[1])
						if (d > itemWidths[1]) itemWidths[1] = d
					}
				}
			}
		} else {
			c = l.count()

			for (t = 0; t < c; t++) {
				s = l.items[t]

				if (this.showHidden || s.visible) {
					d = f.textWidth(s.titleText(t))
					if (d > itemWidths[0]) itemWidths[0] = d
				}
			}
		}

		itemWidth = itemWidths[0] + itemWidths[1]
	}

	/**
	 * @returns {Number} Returns the distance in pixels between legend and chart bounds.
	 */
	this.calcPadding = function (r) {
		// var p=this.padding, n = parseFloat( (p.indexOf("%") == -1) ? p : p.substring(0,p.length-1));

		return 0.01 * this.padding * (this.isVertical() ? r.width : r.height)
	}

	/**
	 * @returns {Boolean} Returns if legend orientation is vertical.
	 */
	this.isVertical = function () {
		var p = this.position
		return p === "right" || p === "left"
	}

	this.calcrect = function () {
		var titleWidth = 0,
			t = this.title,
			r = chart.chartRect,
			a = this.align,
			b = this.bounds,
			vert = this.isVertical()

		if (t.shouldDraw()) {
			t.resize()
			titleHeight = t.bounds.height
			titleWidth = t.bounds.width
		} else titleHeight = 0

		if (vert) b.y = a === 0 ? r.y : chart.bounds.height * a * 0.01
		else b.x = a === 0 ? r.x : chart.bounds.width * a * 0.01

		f.font.prepare()
		this.itemHeight = f.textHeight("Wj")

		this.calcWidths()

		var pad = this.calcPadding(r),
			s,
			co = this.itemsCount()

		if (vert) {
			s = this.symbol.visible ? this.symbol.width + this.symbol.padding : 0

			b.width = Math.max(titleWidth, 12 + itemWidth + s)

			b.height = (0.5 + co) * this.itemHeight + titleHeight

			if (b.width - 6 > titleWidth) t.bounds.width = b.width - 6
		} else {
			b.width = pad + this.perRow * this.totalWidth()
			b.x += 0.5 * (r.width - b.width)
			b.height = this.itemHeight * (this.rows + 0.25)
		}

		if (f.stroke.fill !== "") {
			s = +f.stroke.size
			if (s > 1) {
				b.width += s
				b.height += s
				this.innerOff = s * 0.5
			}
		}

		// Resize chartRect:

		if (co === 0) return

		if (this.position === "right") {
			b.x = r.getRight() - b.width - this.margin * b.width * 0.01
			if (r.automatic) r.setRight(Math.max(r.x, b.x - pad))
		} else if (this.position === "left") {
			b.x = r.x
			if (r.automatic) r.setLeft(b.x + b.width + pad)
		} else if (this.position === "top") {
			b.y = r.y + pad
			if (r.automatic) r.setTop(b.getBottom() + pad)
		} else {
			b.y = r.getBottom() - b.height - pad
			if (r.automatic) r.setBottom(b.y - pad)
		}
	}
}
